import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import {
  Button,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useTheme,
  ButtonProps
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { Box } from '@mui/system';

import InputKeywordOrHashtag from '../InputKeywordOrHashtag';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ProductTourTooltip from '../ProductTourTooltip';
import { reducerUpdateNextTourSteps } from 'src/features/productTour';
import { TStreamType } from 'src/models/general';
import NotAllowedTooltip from '../NotAllowedTooltip';
import { t } from 'i18next';

interface Props {
  title: string;
  onHide?: () => void;
  buttonLabel?: string;
  initialValues?: any;
  data?: any;
  isEditing?: boolean;
  isHidden?: boolean;
  buttonProps?: ButtonProps;
  streamType?: TStreamType;
}

const ModalAddKeywordAndHashtag: React.FC<Props> = ({
  title,
  onHide,
  buttonLabel = 'Keyword',
  initialValues,
  data,
  isEditing = false,
  isHidden = false,
  buttonProps = {},
  streamType
}) => {
  const [open, setOpen] = useState(false);

  const dispatch = useAppDispatch();
  const {
    isOnTour,
    currentTour: { stepNumber, menu, submenu }
  } = useAppSelector((state) => state.storeProductTour);

  const isCurrentlyOnTourStep6 =
    stepNumber === 6 && isOnTour && submenu === 'object';
  const isCurrentlyOnTourStep7 =
    stepNumber === 7 && isOnTour && submenu === 'object';

  const handleClickOpen = () => {
    setOpen(true);
    if (isCurrentlyOnTourStep6) {
      dispatch(reducerUpdateNextTourSteps());
    }
  };

  const handleClose = () => {
    if (isEditing) {
      onHide();
    }
    setOpen(false);
  };

  const theme = useTheme();

  const tempInputObjects = useAppSelector(
    (state) => state.objectManagement.tempInputObjects
  );

  const { advanceObjectQuota, advanceObjectQuotaUsed } = useAppSelector(
    (state) => state.users.project.onBoarding
  );

  const isOnBoarding = useAppSelector(
    (state) => state.users.project.onBoarding.isOnBoarding
  );

  const userRole = useAppSelector((state) => state.users.project.role);

  const isTourGroup = isOnTour && submenu === 'group';
  const isTourSocmedOverview =
    isOnTour && menu === 'social-media' && submenu === 'overview';
  const isMember = userRole === 'member';

  return (
    <>
      {isHidden ? null : (
        <ProductTourTooltip placement="left-end" open={isCurrentlyOnTourStep6}>
          {isMember ? (
            <NotAllowedTooltip>
              <Button
                variant="outlined"
                endIcon={<AddIcon />}
                {...buttonProps}
                sx={
                  isCurrentlyOnTourStep6
                    ? {
                      ...buttonProps.sx,
                      position: 'relative',
                      zIndex: 1000
                    }
                    : { ...buttonProps.sx }
                }
              >
                {t(buttonLabel.toLowerCase())}
              </Button>
            </NotAllowedTooltip>
          ) : (
            <Button
              variant="outlined"
              onClick={
                !(isTourGroup && isTourSocmedOverview)
                  ? handleClickOpen
                  : undefined
              }
              endIcon={<AddIcon />}
              {...buttonProps}
              sx={
                isCurrentlyOnTourStep6
                  ? {
                    ...buttonProps.sx,
                    position: 'relative',
                    zIndex: 1000
                  }
                  : { ...buttonProps.sx }
              }
            >
              {t(buttonLabel.toLowerCase())}
            </Button>
          )}
        </ProductTourTooltip>
      )}
      <Dialog
        onClose={!isOnTour ? handleClose : undefined}
        open={isEditing ? isEditing : open}
        fullWidth
        maxWidth={'md'}
      >
        <IconButton
          aria-label="close"
          onClick={!isOnTour ? handleClose : undefined}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <ProductTourTooltip
          placement="top"
          open={isCurrentlyOnTourStep7}
          onClick={handleClose}
          onClose={handleClose}
        >
          <DialogTitle
            sx={{
              m: !isOnTour ? theme.spacing(3, 3, 0) : undefined,
              p: theme.spacing(2, 3, 0, 3)
            }}
          >
            <Typography variant="h4">
              {title}
              <span
                style={{
                  marginLeft: theme.spacing(1),
                  fontWeight: 400,
                  color: theme.palette.primary.main
                }}
              >
                {advanceObjectQuotaUsed}
              </span>
              {!isOnBoarding && tempInputObjects.length > 0 && (
                <span
                  style={{
                    fontWeight: 400,
                    color: theme.palette.success.main
                  }}
                >
                  {`(+${tempInputObjects.length})`}
                </span>
              )}
              /
              <span
                style={{
                  fontWeight: 400
                }}
              >
                {advanceObjectQuota}
              </span>
            </Typography>
          </DialogTitle>
        </ProductTourTooltip>
        <DialogContent
          sx={{
            m: { xs: 0, sm: theme.spacing(1, 3) }
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="caption">
                {t('your quota is defined when your payment plan is purchased.')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <InputKeywordOrHashtag
                  onClose={handleClose}
                  tempValues={initialValues}
                  isEditing={isEditing}
                  dataList={data}
                  streamType={streamType}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModalAddKeywordAndHashtag;
